import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  Chip,
  Checkbox,
  Select,
  MenuItem,
  Slider,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { LocalizationProvider, DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../contexts/AuthContext';
import {
  announcementApi,
  AnnouncementScope,
  AnnouncementSeverity,
  CreateQuickSaleAnnouncementDto,
  QuickSaleAnnouncementDto,
  RecurrencePattern,
} from '../../../../../services/announcementApiService';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  /** edit modunda hedef duyuru. */
  initialData?: QuickSaleAnnouncementDto | null;
  /** per-screen modaldan açıldıysa bu ekrana zorla scope='Screen' set edilir. */
  fixedScreenId?: number | null;
  /** per-screen başlığı için yardımcı isim. */
  fixedScreenName?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const ALL_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
type WeekdayNum = (typeof ALL_WEEKDAYS)[number];

const FAR_FUTURE_DATE_ISO = '2099-12-31T23:59:59';

function dateToIsoLocal(d: Date | null): string {
  if (!d) return new Date().toISOString();
  // Local time’a saygılı ISO (Z eklemiyoruz — backend DateTime.Now ile karşılaştırıyor)
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function timeToHHmmss(d: Date | null): string | null {
  if (!d) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

function hhmmssToDate(s?: string | null): Date | null {
  if (!s) return null;
  const [hh, mm] = s.split(':');
  const d = new Date();
  d.setHours(parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, 0, 0);
  return d;
}

const AnnouncementFormModal: React.FC<Props> = ({
  open,
  mode,
  initialData,
  fixedScreenId,
  fixedScreenName,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AnnouncementSeverity>('Info');

  const [scope, setScope] = useState<AnnouncementScope>('Company');
  const [selectedBranches, setSelectedBranches] = useState<{ id: string; name: string }[]>([]);

  const [startAt, setStartAt] = useState<Date>(() => new Date());
  const [endAt, setEndAt] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d;
  });
  const [startNow, setStartNow] = useState(true);
  const [indefinite, setIndefinite] = useState(false);

  const [useDailyTime, setUseDailyTime] = useState(false);
  const [dailyStart, setDailyStart] = useState<Date | null>(() => hhmmssToDate('09:00:00'));
  const [dailyEnd, setDailyEnd] = useState<Date | null>(() => hhmmssToDate('17:00:00'));

  const [weekdays, setWeekdays] = useState<WeekdayNum[]>([...ALL_WEEKDAYS]);
  const [recurrence, setRecurrence] = useState<RecurrencePattern>('Daily');
  const [monthDay, setMonthDay] = useState<number>(1);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const branchOptions = useMemo(
    () => branches.filter((b) => b.id !== 'null').map((b) => ({ id: b.id, name: b.name })),
    [branches]
  );

  // Per-screen modaldan açıldıysa scope kilitle
  const isPerScreen = !!fixedScreenId;

  // Edit modunda mevcut veriyi formla doldur
  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && initialData) {
      setTitle(initialData.title || '');
      setMessage(initialData.message || '');
      setSeverity(initialData.severity || 'Info');
      setScope(initialData.scope || 'Company');
      setStartAt(initialData.startAt ? new Date(initialData.startAt) : new Date());
      setEndAt(initialData.endAt ? new Date(initialData.endAt) : new Date());
      setStartNow(false);
      setIndefinite(initialData.endAt ? new Date(initialData.endAt).getFullYear() >= 2099 : false);

      const hasDaily = !!initialData.dailyStartTime && !!initialData.dailyEndTime;
      setUseDailyTime(hasDaily);
      setDailyStart(hhmmssToDate(initialData.dailyStartTime));
      setDailyEnd(hhmmssToDate(initialData.dailyEndTime));

      if (initialData.weekdaysCsv) {
        setWeekdays(
          initialData.weekdaysCsv
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => n >= 1 && n <= 7) as WeekdayNum[]
        );
      } else {
        setWeekdays([...ALL_WEEKDAYS]);
      }

      setRecurrence(initialData.recurrencePattern || 'Daily');
      setMonthDay(initialData.monthDay ?? 1);

      // Branches scope ise mevcut hedefleri seç
      if (initialData.scope === 'Branches') {
        const branchTargetIds = (initialData.targets ?? [])
          .filter((tt) => tt.targetType === 'Branch')
          .map((tt) => tt.targetId);
        setSelectedBranches(
          branchOptions.filter((b) => branchTargetIds.includes(b.id))
        );
      } else {
        setSelectedBranches([]);
      }
    } else {
      // create modu — varsayılanlar
      setTitle('');
      setMessage('');
      setSeverity('Info');
      setScope(isPerScreen ? 'Screen' : 'Company');
      setSelectedBranches([]);
      setStartAt(new Date());
      const d = new Date();
      d.setDate(d.getDate() + 7);
      setEndAt(d);
      setStartNow(true);
      setIndefinite(false);
      setUseDailyTime(false);
      setDailyStart(hhmmssToDate('09:00:00'));
      setDailyEnd(hhmmssToDate('17:00:00'));
      setWeekdays([...ALL_WEEKDAYS]);
      setRecurrence('Daily');
      setMonthDay(1);
    }

    setError(null);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialData, isPerScreen]);

  const handleSubmit = async () => {
    setError(null);

    if (!title.trim() || !message.trim()) {
      setError(t('announcements.form.errorRequired', 'Başlık ve mesaj zorunludur.'));
      return;
    }

    const finalStartAt = startNow ? new Date() : startAt;
    const finalEndAt = indefinite ? new Date(FAR_FUTURE_DATE_ISO) : endAt;
    if (finalEndAt < finalStartAt) {
      setError(t('announcements.form.errorEndBeforeStart', 'Bitiş tarihi başlangıçtan önce olamaz.'));
      return;
    }

    if (scope === 'Branches' && selectedBranches.length === 0) {
      setError(t('announcements.form.errorBranchesRequired', 'En az bir şube seçin.'));
      return;
    }

    if (useDailyTime && (!dailyStart || !dailyEnd)) {
      setError(t('announcements.form.errorDailyTimeIncomplete', 'Günlük başlangıç ve bitiş saatini girin.'));
      return;
    }

    const dto: CreateQuickSaleAnnouncementDto = {
      title: title.trim(),
      message: message.trim(),
      severity,
      startAt: dateToIsoLocal(finalStartAt),
      endAt: dateToIsoLocal(finalEndAt),
      dailyStartTime: useDailyTime ? timeToHHmmss(dailyStart) : null,
      dailyEndTime: useDailyTime ? timeToHHmmss(dailyEnd) : null,
      weekdaysCsv:
        weekdays.length === 0 || weekdays.length === 7
          ? null
          : [...weekdays].sort((a, b) => a - b).join(','),
      recurrencePattern: recurrence,
      monthDay: recurrence === 'Monthly' ? monthDay : null,
      scope,
      branchIds: scope === 'Branches' ? selectedBranches.map((b) => b.id) : [],
      screenIds: scope === 'Screen' && fixedScreenId ? [fixedScreenId] : [],
    };

    setSubmitting(true);
    try {
      if (mode === 'edit' && initialData) {
        await announcementApi.update({ ...dto, id: initialData.id });
      } else {
        await announcementApi.create(dto);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('[AnnouncementFormModal] Save hatası:', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('announcements.form.errorGeneric', 'Duyuru kaydedilemedi.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box>
          {mode === 'edit'
            ? t('announcements.form.titleEdit', 'Duyuruyu Düzenle')
            : t('announcements.form.titleCreate', 'Yeni Duyuru')}
          {isPerScreen && fixedScreenName && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              {t('announcements.form.targetScreenLabel', 'Hedef Ekran')}: {fixedScreenName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label={t('announcements.form.title', 'Başlık')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 256 }}
            />

            <TextField
              label={t('announcements.form.message', 'Mesaj')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
              maxRows={6}
            />

            {/* Severity */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('announcements.form.severity', 'Önem')}
              </Typography>
              <ToggleButtonGroup
                value={severity}
                exclusive
                onChange={(_, v) => v && setSeverity(v)}
                size="small"
              >
                <ToggleButton value="Info" sx={{ color: '#1976d2' }}>
                  <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {t('announcements.severity.info', 'Bilgi')}
                </ToggleButton>
                <ToggleButton value="Warning" sx={{ color: '#ed6c02' }}>
                  <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {t('announcements.severity.warning', 'Uyarı')}
                </ToggleButton>
                <ToggleButton value="Critical" sx={{ color: '#d32f2f' }}>
                  <ErrorOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {t('announcements.severity.critical', 'Kritik')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider />

            {/* Scope */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('announcements.form.scope', 'Hedef')}
              </Typography>
              {isPerScreen ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {t('announcements.form.screenLockedHint', 'Bu modal yalnızca seçili ekrana özel duyuru oluşturur.')}
                </Alert>
              ) : (
                <FormControl>
                  <RadioGroup
                    row
                    value={scope}
                    onChange={(e) => setScope(e.target.value as AnnouncementScope)}
                  >
                    <FormControlLabel
                      value="Company"
                      control={<Radio />}
                      label={t('announcements.form.scopeCompany', 'Tüm Şirket')}
                    />
                    <FormControlLabel
                      value="Branches"
                      control={<Radio />}
                      label={t('announcements.form.scopeBranches', 'Belirli Şubeler')}
                    />
                  </RadioGroup>
                </FormControl>
              )}

              {scope === 'Branches' && !isPerScreen && (
                <Autocomplete
                  multiple
                  options={branchOptions}
                  value={selectedBranches}
                  onChange={(_, v) => setSelectedBranches(v)}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderTags={(value, getTagProps) =>
                    value.map((option, idx) => (
                      <Chip variant="outlined" label={option.name} {...getTagProps({ index: idx })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('announcements.form.branchesPlaceholder', 'Şubeler')}
                      placeholder={t('announcements.form.branchesSelect', 'Şube seçin...')}
                      sx={{ mt: 1 }}
                    />
                  )}
                />
              )}
            </Box>

            <Divider />

            {/* Date Range */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('announcements.form.dateRange', 'Yayın Tarih Aralığı')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <FormControlLabel
                    control={<Checkbox checked={startNow} onChange={(e) => setStartNow(e.target.checked)} />}
                    label={t('announcements.form.startNow', 'Şimdiden başla')}
                  />
                  <DateTimePicker
                    label={t('announcements.form.startAt', 'Başlangıç')}
                    value={startAt}
                    onChange={(d) => d && setStartAt(d)}
                    disabled={startNow}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControlLabel
                    control={<Checkbox checked={indefinite} onChange={(e) => setIndefinite(e.target.checked)} />}
                    label={t('announcements.form.indefinite', 'Süresiz')}
                  />
                  <DateTimePicker
                    label={t('announcements.form.endAt', 'Bitiş')}
                    value={endAt}
                    onChange={(d) => d && setEndAt(d)}
                    disabled={indefinite}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Box>
              </Stack>
            </Box>

            {/* Daily Time */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox checked={useDailyTime} onChange={(e) => setUseDailyTime(e.target.checked)} />
                }
                label={t('announcements.form.useDailyTime', 'Günlük belirli saatlerde göster (her gün tekrar)')}
              />
              {useDailyTime && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                  <TimePicker
                    label={t('announcements.form.dailyStartTime', 'Günlük Başlangıç Saati')}
                    value={dailyStart}
                    onChange={(d) => setDailyStart(d)}
                    ampm={false}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                  <TimePicker
                    label={t('announcements.form.dailyEndTime', 'Günlük Bitiş Saati')}
                    value={dailyEnd}
                    onChange={(d) => setDailyEnd(d)}
                    ampm={false}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Stack>
              )}
            </Box>

            {/* Weekdays */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('announcements.form.weekdays', 'Haftanın Günleri')}
              </Typography>
              <ToggleButtonGroup
                value={weekdays}
                onChange={(_, v) => setWeekdays((v as WeekdayNum[]) ?? [])}
                size="small"
              >
                {ALL_WEEKDAYS.map((wd) => (
                  <ToggleButton key={wd} value={wd}>
                    {t(`announcements.weekdays.${wd}`,
                      wd === 1 ? 'Pzt' : wd === 2 ? 'Sal' : wd === 3 ? 'Çar' : wd === 4 ? 'Per'
                        : wd === 5 ? 'Cum' : wd === 6 ? 'Cmt' : 'Paz'
                    )}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Recurrence */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('announcements.form.recurrence', 'Tekrar Deseni')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={recurrence}
                    onChange={(e) => setRecurrence(e.target.value as RecurrencePattern)}
                  >
                    <MenuItem value="Once">{t('announcements.recurrence.once', 'Tek Seferlik')}</MenuItem>
                    <MenuItem value="Daily">{t('announcements.recurrence.daily', 'Her Gün')}</MenuItem>
                    <MenuItem value="Weekly">{t('announcements.recurrence.weekly', 'Haftalık')}</MenuItem>
                    <MenuItem value="Monthly">{t('announcements.recurrence.monthly', 'Aylık')}</MenuItem>
                  </Select>
                </FormControl>
                {recurrence === 'Monthly' && (
                  <Box sx={{ flex: 1, minWidth: 250 }}>
                    <Typography variant="caption">
                      {t('announcements.form.monthDay', 'Ayın günü')}: <strong>{monthDay}</strong>
                    </Typography>
                    <Slider
                      value={monthDay}
                      onChange={(_, v) => setMonthDay(v as number)}
                      min={1}
                      max={31}
                      step={1}
                      marks={[{ value: 1, label: '1' }, { value: 15, label: '15' }, { value: 31, label: '31' }]}
                    />
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('announcements.form.cancel', 'İptal')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {mode === 'edit'
            ? t('announcements.form.save', 'Kaydet')
            : t('announcements.form.create', 'Oluştur')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementFormModal;
